import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { PanelRightClose } from "lucide-react";
import { isAxiosError } from "axios";
import { queue } from "@/components/Toast";
import { usePermissions } from "@/hooks/usePermissions";
import { useSessionAttachments } from "@/app/routes/dashboard/api/useSessionAttachments";
import { useDeferredSend } from "@/app/routes/dashboard/api/useDeferredSend";
import { findPendingInteraction } from "@/app/routes/dashboard/components/agentInteractions";
import { canonicalizeChatModelId, useChatModels } from "@/hooks/useChatModels";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import type { NavigateTarget } from "@/types/agentFocus.type";
import {
  registerAgentRailHandlers,
  useAgentFocusStore,
  type AgentLocalAsk,
  type AgentReplyTarget,
  type AgentSendOptions,
} from "./agentFocusStore";
import { focusLabel, focusPlaceholder, focusSuggestions } from "./focusLabel";
import { targetToPath } from "./navigateTarget";
import { useBrandAgentChat } from "./useBrandAgentChat";
import { useStrategySlideContext } from "../strategy/strategySlideContext";
import { useAgentPanelStore } from "./agentPanelStore";
import { useBrandAgentSessions } from "./useBrandAgentSessions";
import RailPanel from "./rail/RailPanel";
import { LocalAskTurn } from "./rail/RailParts";
import { DeskTab, PhoneCorner } from "./rail/RailEntry";
import { usePhoneLaneSlot } from "./phoneLaneSlot";
import RailTabs from "./rail/RailTabs";
import { railTabFrom, useRailCommentsStore, withRailTab, type RailTab } from "./railCommentsStore";
import { sheetStyle, useSheetDetents } from "./rail/useSheetDetents";
import { placeAttachments, waitingOn, type AttachmentClaims } from "./rail/railFormat";
import { useVoiceRecorder, voiceSupported } from "./rail/useVoiceRecorder";
import { transcribeVoiceNote } from "./rail/voiceTranscribe";
import type { ComposerVoice } from "./rail/RailComposer";
import "./rail/rail.css";

/* Below this a column is impossible, so the panel becomes a sheet over the
   field. Matches COMPACT_SHELL in CockpitLayout. */
const SHEET_MODE = "(max-width: 1023px)";

/** Puts a scroller back where it was, or at its end when nothing was kept. */
function restoreScroll(el: HTMLElement | null, top: number | null) {
  if (el) el.scrollTop = top ?? el.scrollHeight;
}

const sessionStorageKey = (brandId: string) => `co:agent:session:${brandId}`;

function readPersistedSessionId(brandId: string): string | null {
  try {
    return localStorage.getItem(sessionStorageKey(brandId));
  } catch {
    return null;
  }
}

function writePersistedSessionId(brandId: string, sessionId: string | null) {
  try {
    if (sessionId) localStorage.setItem(sessionStorageKey(brandId), sessionId);
    else localStorage.removeItem(sessionStorageKey(brandId));
  } catch {
    /* best effort; a blocked localStorage just loses the last-open chat */
  }
}

/**
 * The one agent rail, mounted by the shell.
 *
 * It is deliberately NOT owned by a page. Pages come and go as the user
 * navigates; the conversation does not. Each page publishes where the user is
 * (useAgentFocus) and the rail sends that with every message, which is what
 * lets one chat carry the user from the universe to a strategy to a month
 * without losing its transcript, its draft, or a reply mid-stream.
 *
 * This component owns the live chat. How it looks lives in rail/RailPanel,
 * which the dev preview renders from fixtures.
 */
export default function BrandAgentRail({ brandId }: { brandId: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const focusedReview = location.pathname.includes("/content-calendar") &&
    ["concepts", "client-preview", "review"].includes(new URLSearchParams(location.search).get("view") ?? "");
  const focus = useAgentFocusStore((s) => s.focus);
  const selectedSlide = useStrategySlideContext((s) => (s.cleared ? null : s.current));
  const slideSelection = useStrategySlideContext((s) => s.selection);
  const slideContext = focus.surface === "strategy" && selectedSlide?.strategyId === focus.strategyId && selectedSlide?.brandId === brandId ? selectedSlide : null;
  // The open slide and any words highlighted on it ride with the next message.
  const composerContext = slideContext
    ? [
        {
          key: "slide",
          label: "Slide",
          quote: slideContext.label,
          clearLabel: "Ask about the whole strategy instead",
          onClear: () => useStrategySlideContext.getState().dismiss(),
        },
        ...(slideSelection
          ? [{
              key: "selection",
              label: "Selected text",
              quote: `“${slideSelection}”`,
              clearLabel: "Leave out the selected text",
              onClear: () => useStrategySlideContext.getState().setSelection(null),
            }]
          : []),
      ]
    : undefined;
  const open = useAgentPanelStore((st) => st.open);
  const setOpen = useAgentPanelStore((st) => st.setOpen);
  const [previousReview, setPreviousReview] = useState(focusedReview);
  if (previousReview !== focusedReview) {
    setPreviousReview(focusedReview);
    if (focusedReview) setOpen(false);
  }
  const isSheet = useMediaQuery(SHEET_MODE);
  const laneSlot = usePhoneLaneSlot((s) => s.slot);
  /* A post page registers its comments; on the desk the rail's head then
     becomes Comments and Chat with M7 (the sheet layouts show the page's own
     comments sheet instead). The open tab lives in the URL. */
  const commentsInfo = useRailCommentsStore((st) => st.info);
  const setCommentsSlot = useRailCommentsStore((st) => st.setSlot);
  const commentsActive = !!commentsInfo && !isSheet;
  const [searchParams, setSearchParams] = useSearchParams();
  const railTab: RailTab = commentsActive ? railTabFrom(searchParams) : "chat";
  /* The chat is display:none while Comments is open, which drops its scroll
     position; it is kept here and put back when the chat shows again. */
  const chatScrollTop = useRef<number | null>(null);
  const setRailTab = (tab: RailTab) => {
    if (tab === "comments" && railTab === "chat" && scrollRef.current) {
      chatScrollTop.current = scrollRef.current.scrollTop;
    }
    setSearchParams((previous) => withRailTab(previous, tab), { replace: true });
  };
  const wantsComments = commentsActive && railTab === "comments";
  const [text, setText] = useState("");
  /* A direction pinned to one thing on the page ("Replying to Idea 2"): the
     next send goes to the page's handler, not to the chat. */
  const [replyTarget, setReplyTarget] = useState<AgentReplyTarget | null>(null);
  /* A page's own question at the end of the chat (Add a post): answered in
     the rail, with no model call. */
  const [localAsk, setLocalAsk] = useState<AgentLocalAsk | null>(null);
  const askScrollTimer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (askScrollTimer.current !== null) window.clearTimeout(askScrollTimer.current);
    },
    [],
  );
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const sheetRef = useRef<HTMLElement>(null);
  const { data: models = [] } = useChatModels();

  const [selectedSessionId, setSelectedSessionIdState] = useState<
    string | null | undefined
  >(() => readPersistedSessionId(brandId) ?? undefined);

  // The rail is keyed on brandId by the shell, so a brand switch remounts it;
  // this only covers the case where React reuses the instance instead.
  const [boundBrandId, setBoundBrandId] = useState(brandId);
  if (brandId !== boundBrandId) {
    setBoundBrandId(brandId);
    setSelectedSessionIdState(readPersistedSessionId(brandId) ?? undefined);
    setText("");
  }

  const setSelectedSessionId = (id: string | null | undefined) => {
    setSelectedSessionIdState(id);
    writePersistedSessionId(brandId, id ?? null);
  };

  const {
    sessions,
    createSession,
    deleteSession,
    renameSession,
    isLoading: sessionsLoading,
    invalidateSessions,
  } = useBrandAgentSessions(brandId);

  const currentSessionId = useMemo(() => {
    if (selectedSessionId === null) return null; // explicit new chat
    if (selectedSessionId && sessions.some((s) => s.id === selectedSessionId)) {
      return selectedSessionId;
    }
    if (selectedSessionId && sessions.length === 0) return selectedSessionId;
    return sessions[0]?.id ?? null;
  }, [selectedSessionId, sessions]);

  const handleNavigate = (target: NavigateTarget) => {
    setOpen(true);
    navigate(targetToPath(target));
  };

  const {
    messages,
    isLoadingMessages,
    streamingBlocks,
    isStreaming,
    streamError,
    sendMessage,
    stopStreaming,
    isStopping,
    clearMessages,
    selectedModel,
    setSelectedModel,
    selectedReasoningEffort,
    setSelectedReasoningEffort,
  } = useBrandAgentChat(brandId, currentSessionId, focus, {
    onSessionCreated: (s) => setSelectedSessionId(s.id),
    onSessionsChanged: invalidateSessions,
    onNavigate: handleNavigate,
  });

  const deletingSessionId = deleteSession.isPending
    ? (deleteSession.variables ?? null)
    : null;
  const isDeletingActiveChat =
    deletingSessionId !== null && deletingSessionId === currentSessionId;
  const isInitialChatLoading = sessionsLoading || isLoadingMessages;
  const isChatBusy = isStreaming || isInitialChatLoading || isDeletingActiveChat;

  // The agent put a card up and is waiting on it. Holding the composer shut is
  // what stops a second card being raised on top of an unanswered one.
  const awaitingResponse = findPendingInteraction(messages) !== null;

  const attachments = useSessionAttachments(brandId, currentSessionId);

  /* The files waiting in the composer go out with the message being sent. They
     are claimed by that message's position among yours, because its time comes
     from this device and the files' from the server. */
  const [claims, setClaims] = useState<AttachmentClaims>({});
  const claimStaged = () => {
    const position = messages.filter((m) => m.role === "user").length;
    const { staged } = placeAttachments(messages, attachments.items, claims);
    const ids = staged.filter((item) => !item.local).map((item) => item.id);
    if (ids.length === 0) return;
    setClaims((prev) => ({ ...prev, ...Object.fromEntries(ids.map((id) => [id, position])) }));
  };

  /* Talk, then check the words (Figma 05 · Voice). Voice is only a way to
     type: the backend writes the recording down, the words land in the field
     to check before sending, and the recording itself is never shown or sent. */
  const { canEdit } = usePermissions();
  const [canTalk] = useState(voiceSupported);
  const recorder = useVoiceRecorder();
  const [voiceStage, setVoiceStage] = useState<"idle" | "writing" | "checking">("idle");

  /* A message that brought files (the strategy start's PDF) waits for its new
     chat to open, then queues like any message with a file still being read. */
  const [firstSend, setFirstSend] = useState<{ sessionId: string; text: string } | null>(null);

  const { isQueued, queueOrSend } = useDeferredSend({
    attachments: attachments.items,
    sessionId: currentSessionId,
    onSend: (t) => {
      claimStaged();
      void sendMessage(t);
    },
  });
  const sendBusy = isChatBusy || isQueued;
  useEffect(() => {
    if (!firstSend || currentSessionId !== firstSend.sessionId || isInitialChatLoading) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot hand-off once the new chat is open
    setFirstSend(null);
    queueOrSend(firstSend.text);
  }, [firstSend, currentSessionId, isInitialChatLoading, queueOrSend]);

  const monthPlanned = focus.surface === "calendar" && Boolean(focus.planId);
  const placeholder = awaitingResponse
    ? "Answer above to continue"
    : focusPlaceholder(focus, monthPlanned);
  const label = focusLabel(focus);
  const shortLabel = focusLabel(focus, true);

  const close = () => setOpen(false);
  const { detent, settled, setDetent, onDragStart } = useSheetDetents(sheetRef, close);

  /* Pages hand the rail a message or a draft through registered handlers rather
     than a queue in the store: a queue has to be drained from an effect, and
     draining it means calling this component's setState from inside one. */
  const railHandlers = {
    send: (content: string, opts?: AgentSendOptions) => {
      if (sendBusy || awaitingResponse || firstSend) return;
      setOpen(true);
      const files = opts?.files ?? [];
      if (files.length) {
        void (async () => {
          try {
            const session = await createSession.mutateAsync();
            setSelectedSessionId(session.id);
            await attachments.upload(files, session.id);
            setFirstSend({ sessionId: session.id, text: content });
          } catch {
            setText(content);
          }
        })();
        return;
      }
      const forcedModelId = opts?.modelId;
      const forcedModel = forcedModelId
        ? (models.find((m) => m.id === canonicalizeChatModelId(forcedModelId)) ?? null)
        : undefined;
      if (opts?.sessionId) setSelectedSessionId(opts.sessionId);
      if (opts?.newSession) setSelectedSessionId(null);
      // Files waiting in this chat go with a message sent to this chat.
      if (!opts?.newSession && (!opts?.sessionId || opts.sessionId === currentSessionId)) {
        claimStaged();
      }
      void sendMessage(content, {
        ...(forcedModel !== undefined ? { model: forcedModel } : {}),
        ...(opts?.reasoningEffort ? { reasoningEffort: opts.reasoningEffort } : {}),
        ...(opts?.newSession ? { newSession: true } : {}),
        ...(opts?.sessionId ? { sessionId: opts.sessionId } : {}),
      });
    },
    prefill: (content: string, opts?: { newChat?: boolean }) => {
      if (opts?.newChat) handleNewChat();
      setOpen(true);
      setText(content);
      textareaRef.current?.focus();
    },
    replyTo: (target: AgentReplyTarget) => {
      if (commentsActive && railTab !== "chat") setRailTab("chat");
      setReplyTarget(target);
      setText("");
      if (isSheet) setDetent("full");
      setOpen(true);
      // The desk column slides open; the caret waits for it.
      window.setTimeout(() => textareaRef.current?.focus({ preventScroll: true }), 280);
    },
    clearReply: (key?: string) => {
      setReplyTarget((current) => (!current || (key && current.key !== key) ? current : null));
    },
    ask: (ask: AgentLocalAsk) => {
      if (commentsActive && railTab !== "chat") setRailTab("chat");
      setLocalAsk(ask);
      if (isSheet) setDetent("full");
      setOpen(true);
      // The card lands at the end of the chat; bring it into view once it has.
      if (askScrollTimer.current !== null) window.clearTimeout(askScrollTimer.current);
      askScrollTimer.current = window.setTimeout(() => {
        askScrollTimer.current = null;
        const log = scrollRef.current;
        if (log) log.scrollTop = log.scrollHeight;
      }, 60);
    },
    clearAsk: (key?: string) => {
      setLocalAsk((current) => (!current || (key && current.key !== key) ? current : null));
    },
    stop: () => {
      if (isStreaming) stopStreaming();
    },
  };
  // Re-registered every render so the handlers always close over the current
  // session, model list and busy state. Registration is a module assignment,
  // not React state, so doing it from an effect costs nothing and renders nothing.
  useEffect(() => {
    registerAgentRailHandlers(railHandlers);
    return () => registerAgentRailHandlers(null);
  });

  // A link with ?rail=comments opens the rail on Comments, even in focused
  // review where the rail otherwise starts collapsed.
  useEffect(() => {
    if (wantsComments) useAgentPanelStore.getState().setOpen(true);
  }, [wantsComments]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    // A reply that landed behind Comments: come back to it, not to the old spot.
    if (el.offsetParent === null) chatScrollTop.current = null;
  }, [messages, streamingBlocks]);

  // Back on the chat: where you left it, or the newest turn when the page
  // opened on Comments. Before paint, so the transcript never flashes its top.
  const chatVisible = railTab === "chat";
  useLayoutEffect(() => {
    if (!chatVisible) return;
    restoreScroll(scrollRef.current, chatScrollTop.current);
    chatScrollTop.current = null;
  }, [chatVisible]);

  /* The persisted open flag is a DOCKING preference, not a request for a
     sheet. Left docked open on the desk, it would otherwise greet you on a
     phone as a sheet over the work before you had asked for anything. Sheet
     mode always starts closed; the desk remembers. */
  useEffect(() => {
    if (window.matchMedia(SHEET_MODE).matches) useAgentPanelStore.getState().setOpen(false);
  }, []);

  useEffect(() => {
    if (!open || !isSheet) return;
    const onKeyDown = (event: KeyboardEvent) => {
      // Only the sheet sits over the work; a desk column is beside it, so
      // Escape there would close something the user deliberately parked.
      if (event.key !== "Escape") return;
      setOpen(false);
      document.getElementById("m7r-corner")?.focus();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, isSheet, setOpen]);

  const handleNewChat = () => {
    recorder.cancel();
    setVoiceStage("idle");
    setSelectedSessionId(null);
    clearMessages();
  };

  const ensureSessionId = async (): Promise<string | null> => {
    if (currentSessionId) return currentSessionId;
    const session = await createSession.mutateAsync();
    setSelectedSessionId(session.id);
    return session.id;
  };

  const startVoice = async () => {
    try {
      await recorder.start();
    } catch {
      queue.add({
        title: "The microphone is off",
        description: "Allow the microphone for this site, then try again.",
        variant: "error",
      });
    }
  };

  const stopVoice = async () => {
    const file = await recorder.stop();
    if (!file) return;
    setVoiceStage("writing");
    try {
      const words = await transcribeVoiceNote(brandId, file);
      if (!words) throw new Error("No words were heard. Try again, or type instead.");
      setText((prev) => (prev.trim() ? `${prev.trimEnd()} ${words}` : words));
      setVoiceStage("checking");
      window.setTimeout(() => textareaRef.current?.focus({ preventScroll: true }), 0);
    } catch (error) {
      setVoiceStage("idle");
      queue.add({
        title: "Couldn't write that down",
        description:
          (isAxiosError(error) ? (error.response?.data as { message?: string } | undefined)?.message : null) ??
          (error instanceof Error ? error.message : null) ??
          "Try again, or type instead.",
        variant: "error",
      });
    }
  };
  // A note that runs to the cap stops itself, through the same path as Stop.
  useEffect(() => {
    recorder.autoStop.current = () => void stopVoice();
  });

  const composerVoice: ComposerVoice | undefined =
    canEdit && canTalk
      ? {
          phase: recorder.recording
            ? "recording"
            : voiceStage === "checking" && !text.trim()
              ? "idle"
              : voiceStage,
          levels: recorder.levels,
          elapsedMs: recorder.elapsedMs,
          onStart: () => void startVoice(),
          onStop: () => void stopVoice(),
          onCancel: recorder.cancel,
        }
      : undefined;

  const handleAttachFiles = async (files: File[]) => {
    setOpen(true);
    const sid = await ensureSessionId();
    if (sid) void attachments.upload(files, sid);
  };

  const handleSend = () => {
    const trimmed = text.trim();
    if (replyTarget) {
      if (!trimmed) return;
      setText("");
      setReplyTarget(null);
      replyTarget.onSend(trimmed);
      return;
    }
    if (sendBusy || awaitingResponse || !trimmed || voiceStage === "writing") return;
    setOpen(true);
    setText("");
    setVoiceStage("idle");
    // Holds the turn until any just-attached PDF finishes analyzing, then fires.
    queueOrSend(trimmed);
  };

  const handleDeleteSession = async (sessionId: string) => {
    const wasActive = sessionId === currentSessionId;
    await deleteSession.mutateAsync(sessionId);
    if (wasActive) {
      setSelectedSessionId(undefined);
      clearMessages();
    }
  };

  const status = {
    working: isStreaming,
    waiting: isStreaming ? null : waitingOn(messages),
  };

  const openFromTab = () => {
    if (commentsActive && railTab !== "chat") setRailTab("chat");
    setOpen(true);
    // The desk column slides open; the caret waits for it.
    window.setTimeout(() => textareaRef.current?.focus({ preventScroll: true }), 280);
  };

  const toggleSheet = () => {
    if (open) {
      setOpen(false);
      return;
    }
    // A waiting card needs room; everything else opens at half height.
    setDetent(status.waiting ? "full" : "half");
    setOpen(true);
    requestAnimationFrame(() => sheetRef.current?.focus({ preventScroll: true }));
  };

  const sheetOpenFull = isSheet && open && detent === "full";

  return (
    <>
      <div
        className="co-agent-scrim"
        aria-hidden="true"
        data-open={sheetOpenFull ? "true" : "false"}
        onClick={close}
      />

      {/* In the corner, or in the page's own top lane where it keeps a slot. */}
      {isSheet
        ? laneSlot
          ? createPortal(<PhoneCorner open={open} status={status} onToggle={toggleSheet} inLane />, laneSlot)
          : <PhoneCorner open={open} status={status} onToggle={toggleSheet} />
        : null}

      <div className="co-agent-dock" data-open={open ? "true" : "false"}>
        <DeskTab
          label={shortLabel}
          followLabel={label}
          open={open}
          status={status}
          onOpen={openFromTab}
          comments={
            commentsActive && commentsInfo
              ? {
                  count: commentsInfo.count,
                  onOpen: () => {
                    setRailTab("comments");
                    setOpen(true);
                  },
                }
              : null
          }
        />

        <aside
          ref={sheetRef}
          id="co-agent-panel"
          className="co-agent-dock-inner"
          aria-label="Creo"
          tabIndex={-1}
          data-detent={isSheet ? detent : undefined}
          style={isSheet ? sheetStyle(open, detent, settled) : undefined}
          /* Kept mounted so the draft, the transcript and the scroll position
             survive a collapse; inert keeps it off the tab order while hidden. */
          inert={!open}
        >
          {commentsActive && commentsInfo ? (
            <RailTabs
              tab={railTab}
              onTab={setRailTab}
              count={commentsInfo.count}
              subtitle={commentsInfo.subtitle}
              collapse={
                <button
                  type="button"
                  className="co-railtabs-collapse"
                  aria-label="Collapse the rail"
                  title="Collapse"
                  onClick={() => {
                    close();
                    document.getElementById("co-agent-tab")?.focus();
                  }}
                >
                  <PanelRightClose aria-hidden strokeWidth={1.75} />
                </button>
              }
            />
          ) : null}
          <RailPanel
            tabPanel={
              commentsActive
                ? { id: "co-rail-panel-chat", labelledBy: "co-rail-tab-chat", hidden: railTab !== "chat" }
                : undefined
            }
            mode={isSheet ? "sheet" : "column"}
            followLabel={shortLabel}
            sessions={sessions}
            currentSessionId={currentSessionId}
            onSelectSession={(id) => setSelectedSessionId(id)}
            onNewChat={handleNewChat}
            onDeleteSession={(id) => void handleDeleteSession(id)}
            onRenameSession={(sessionId, title) => renameSession.mutate({ sessionId, title })}
            deletingSessionId={deletingSessionId}
            historyDisabled={isInitialChatLoading || isStreaming || isDeletingActiveChat}
            onClose={() => {
              close();
              if (isSheet) document.getElementById("m7r-corner")?.focus();
              else document.getElementById("co-agent-tab")?.focus();
            }}
            messages={messages}
            isLoading={isInitialChatLoading}
            isDeleting={isDeletingActiveChat}
            isStreaming={isStreaming}
            streamingBlocks={streamingBlocks}
            streamError={streamError}
            attachments={attachments.items}
            linkedAttachments={attachments.linked}
            attachmentClaims={claims}
            onRetryAttachment={(id) => void attachments.retry(id)}
            onRemoveAttachment={(id) => void attachments.remove(id)}
            onRespond={(reply) => queueOrSend(reply)}
            respondDisabled={sendBusy}
            models={models}
            suggestions={focusSuggestions(focus, monthPlanned)}
            onStarter={(suggestion) => {
              if (sendBusy || awaitingResponse) return;
              setOpen(true);
              queueOrSend(suggestion);
            }}
            startersDisabled={sendBusy || awaitingResponse}
            composer={{
              value: text,
              onChange: setText,
              onSend: handleSend,
              placeholder: replyTarget?.placeholder ?? placeholder,
              inputLabel: replyTarget ? `${replyTarget.label}: ${replyTarget.quote}` : `Message Creo about ${label}`,
              disabled: replyTarget ? false : sendBusy || awaitingResponse,
              replyTo: replyTarget
                ? { label: replyTarget.label, quote: replyTarget.quote, onClear: () => setReplyTarget(null) }
                : undefined,
              context: composerContext,
              // A reply goes to the page, so a reply mid-stream in the chat does not hold it.
              isStreaming: replyTarget ? false : isStreaming,
              onStop: stopStreaming,
              isStopping,
              models,
              selectedModel,
              onModelChange: setSelectedModel,
              selectedReasoningEffort,
              onReasoningEffortChange: setSelectedReasoningEffort,
              textareaRef,
              onAttachFiles: (files) => void handleAttachFiles(files),
              attachDisabled: isStreaming,
              queued: isQueued,
              onFieldFocus: isSheet ? () => setDetent("full") : undefined,
              voice: replyTarget ? undefined : composerVoice,
            }}
            onDropFiles={(files) => void handleAttachFiles(files)}
            dropDisabled={isStreaming}
            scrollRef={scrollRef}
            onSheetDragStart={isSheet ? onDragStart : undefined}
            localTurn={
              localAsk ? (
                <LocalAskTurn key={localAsk.key} prompt={localAsk.prompt}>
                  {localAsk.card(() => setLocalAsk((current) => (current?.key === localAsk.key ? null : current)))}
                </LocalAskTurn>
              ) : null
            }
          />
          {commentsActive ? (
            <div
              ref={setCommentsSlot}
              id="co-rail-panel-comments"
              role="tabpanel"
              aria-labelledby="co-rail-tab-comments"
              className="co-rail-comments-slot"
              hidden={railTab !== "comments"}
            />
          ) : null}
        </aside>
      </div>
    </>
  );
}
